// TariffSelector.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Card,
  CardActionArea,
  CardContent,
  Tooltip,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import HomeIcon from '@mui/icons-material/Home';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import FactoryIcon from '@mui/icons-material/Factory';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimelineIcon from '@mui/icons-material/Timeline';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import WbSunnyIcon from '@mui/icons-material/WbSunny';           // gündüz
import WhatshotIcon from '@mui/icons-material/Whatshot';         // puant (en yoğun an)
import NightlightRoundIcon from '@mui/icons-material/NightlightRound'; // gece
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';


const GROUP_PRIORITY = ['Mesken', 'Tarımsal Faaliyetler', 'Sanayi'];
const LABELS = {
  SINGLE_PERIOD: 'Tek Zamanlı',
  MULTI_PERIOD: 'Üç Zamanlı',
  SINGLE_TERM: 'Tek Terimli',
  DOUBLE_TERM: 'Çift Terimli',
  LOW_VOLTAGE: 'Alçak Gerilim',
  MEDIUM_VOLTAGE: 'Orta Gerilim'
};
const ICONS = {
  Mesken: <HomeIcon sx={{ fontSize: 32, mb: 1 }} />,
  'Tarımsal Faaliyetler': <AgricultureIcon sx={{ fontSize: 32, mb: 1 }} />,
  Sanayi: <FactoryIcon sx={{ fontSize: 32, mb: 1 }} />,
  // Tek sayım: büyük tek saat
  SINGLE_PERIOD: <AccessTimeIcon sx={{ fontSize: 16, mb: 0.5 }} />,
  // Üç zamanlı: yatayda 3 küçük saat
  MULTI_PERIOD: (
    <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
      <WbSunnyIcon sx={{ fontSize: 16 }} titleAccess="Gündüz" />
      <WhatshotIcon sx={{ fontSize: 16 }} titleAccess="Puant" />
      <NightlightRoundIcon sx={{ fontSize: 16 }} titleAccess="Gece" />
    </Box>
  ),
  SINGLE_TERM: <TimelineIcon sx={{ fontSize: 12, mb: 1 }} />,
  DOUBLE_TERM: <TimelineIcon sx={{ fontSize: 12, mb: 1 }} />,
  LOW_VOLTAGE: <ElectricBoltIcon sx={{ fontSize: 12, mb: 1 }} />,
  MEDIUM_VOLTAGE: <ElectricBoltIcon sx={{ fontSize: 12, mb: 1 }} />
};

const TariffSelector = ({
  tariffs,
  value,
  onChange,
  onTariffSelected,
  error,
  helperText,
  disabled
}) => {
  // 1) State’ler
  const [group, setGroup] = useState(null);
  const [time, setTime] = useState(null);
  const [term, setTerm] = useState('SINGLE_TERM');          // Varsayılan: tek terimli
  const [voltage, setVoltage] = useState('LOW_VOLTAGE');     // Varsayılan: alçak gerilim

  const [matchedTariff, setMatchedTariff] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 2) “value” değişince otomatik seçim
  useEffect(() => {
    if (
      value != null &&
      Array.isArray(tariffs) &&
      tariffs.length > 0
    ) {
      const sel = tariffs.find(t => t.tariffId === value);
      if (sel) {
        setGroup(sel.tariffGroupName);
        setTime(sel.timeOfUseType);
        setTerm(sel.termType);
        setVoltage(sel.voltageLevel);
        setMatchedTariff(sel);
        if (onTariffSelected) onTariffSelected(sel);
        if (onChange) onChange(sel);
      }
    }
  }, [value, tariffs, onTariffSelected, onChange]);

  // 3) Kullanıcı seçim yaptıkça matchedTariff'ı güncelle
  useEffect(() => {
    if (
      group &&
      time &&
      term &&
      voltage &&
      Array.isArray(tariffs) &&
      tariffs.length > 0
    ) {
      const found = tariffs.find(
        t =>
          t.tariffGroupName === group &&
          t.timeOfUseType === time &&
          t.termType === term &&
          t.voltageLevel === voltage
      );
      setMatchedTariff(found || null);
      if (found) {
        if (onTariffSelected) onTariffSelected(found);
        if (onChange) onChange(found);
      }
    } else {
      setMatchedTariff(null);
    }
  }, [group, time, term, voltage, tariffs, onTariffSelected, onChange]);

  // 4) Spinner: "tariffs" gelmemişse veya boşsa
  if (!Array.isArray(tariffs) || tariffs.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  // 5) “tariffGroupName” alanlarını Set ile çıkar
  const allGroups = (() => {
    const s = new Set();
    for (const t of tariffs) {
      if (typeof t.tariffGroupName === 'string' && t.tariffGroupName.trim()) {
        s.add(t.tariffGroupName);
      }
    }
    return Array.from(s);
  })();


  const handleTermChange = (event, newTerm) => {
    if (newTerm !== null) {
      setTerm(newTerm);
    }
  };
  const handleVoltageChange = (event, newVoltage) => {
    if (newVoltage !== null) {
      setVoltage(newVoltage);
    }
  };


  // 6) Birinci öncelikli: Mesken, Tarımsal Faaliyetler, Sanayi
  const primaryGroups = GROUP_PRIORITY;

  // 7) İkinci öncelikli: primary dışında kalanlar
  const secondaryGroups = allGroups.filter(g => !GROUP_PRIORITY.includes(g));

  // 8) Zaman / Terim / Gerilim benzersiz değerleri
  const allTimes = (() => {
    const s = new Set();
    for (const t of tariffs) {
      if (typeof t.timeOfUseType === 'string' && t.timeOfUseType.trim()) {
        s.add(t.timeOfUseType);
      }
    }
    return Array.from(s).sort();
  })();

  const allTerms = (() => {
    const s = new Set();
    for (const t of tariffs) {
      if (typeof t.termType === 'string' && t.termType.trim()) {
        s.add(t.termType);
      }
    }
    return Array.from(s);
  })();

  const allVoltages = (() => {
    const s = new Set();
    for (const t of tariffs) {
      if (typeof t.voltageLevel === 'string' && t.voltageLevel.trim()) {
        s.add(t.voltageLevel);
      }
    }
    return Array.from(s).sort();
  })();

  // 9) Primary kart render fonksiyonu
  const renderPrimaryCard = labelKey => {
    const isSelected = group === labelKey;
    return (
      <Card
        key={labelKey}
        variant="outlined"
        sx={{
          height: 120,                    // Primary ile aynı yükseklik
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderColor: isSelected ? 'primary.main' : 'grey.300',
          backgroundColor: isSelected ? 'primary.50' : 'background.paper',
          boxShadow: isSelected ? 3 : 0,
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'default' : 'pointer'
        }}
        onClick={() => {
          if (!disabled) setGroup(labelKey);
        }}
      >
        <CardActionArea disabled={disabled} sx={{ height: '100%' }}>
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              px: 1,
              py: 2,
              textAlign: 'center'
            }}
          >
            {ICONS[labelKey] || null}
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {LABELS[labelKey] || labelKey}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  // 10) Secondary container: sabit yükseklik 140px, alt–kartlar alt alta, taşan kısım hidden
  const renderSecondaryContainer = () => {
    return (
      <Card
        variant="outlined"
        sx={{
          height: 120,           // Primary ile aynı yükseklik
          border: 'none',        // Dış kartın kenar çizgisini kaldırdık
          boxShadow: 'none',     // Dış kartın gölgesini kaldırdık
          opacity: disabled ? 0.6 : 1,
          display: 'flex',
          overflow: 'hidden',    // Taşan içerik gizlensin
          flexDirection: 'column',
          py: 0,                 // Üst‐alt padding (ör. 8px)
          px: 0                // Sol‐sağ padding (ör. 4px)
        }}
      >
        {secondaryGroups.map(sg => {
          const isSel = group === sg;
          return (
            <Card
              key={sg}
              variant="outlined"
              sx={{
                width: '100%',
                height: 60,               // Her sub‐kart tek satır 60px
                mb: 0.3,
                borderColor: isSel ? 'primary.main' : 'grey.300',
                backgroundColor: isSel ? 'primary.50' : 'background.paper',
                boxShadow: isSel ? 2 : 0,
                opacity: disabled ? 0.6 : 1,
                cursor: disabled ? 'default' : 'pointer'
              }}
              onClick={() => {
                if (!disabled) setGroup(sg);
              }}
            >
              <CardActionArea disabled={disabled} sx={{ height: '100%' }}>
                <CardContent
                  sx={{
                    display: 'flex',
                    alignItems: 'center',

                    justifyContent: 'center',
                    height: '100%',
                    px: 0.5,
                    pt: 0,   // Üst padding sıfır
                    pb: 0,   // Alt padding sıfır
                    textAlign: 'center'
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.75rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {sg}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Card>
    );
  };

  // 11) Zaman Tipi kart render
  const renderTimeCard = labelKey => {
    const isSelected = time === labelKey;
    return (
      <Card
        key={labelKey}
        variant="outlined"
        sx={{
          height: 70,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderColor: isSelected ? 'primary.main' : 'grey.300',
          backgroundColor: isSelected ? 'primary.50' : 'background.paper',
          boxShadow: isSelected ? 3 : 0,
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'default' : 'pointer'
        }}
        onClick={() => {
          if (!disabled) setTime(labelKey);
        }}
      >
        <CardActionArea disabled={disabled} sx={{ height: '100%' }}>
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              px: 1,
              py: 2,
              textAlign: 'center'
            }}
          >
            {ICONS[labelKey] || null}
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {LABELS[labelKey] || labelKey}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  // 12) Terim Tipi kart render
  const renderTermCard = labelKey => {
    const isSel = term === labelKey;
    return (
      <Card
        key={labelKey}
        variant="outlined"
        sx={{
          height: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderColor: isSel ? 'primary.main' : 'grey.300',
          backgroundColor: isSel ? 'primary.50' : 'background.paper',
          boxShadow: isSel ? 3 : 0,
          cursor: disabled ? 'default' : 'pointer'
        }}
        onClick={() => !disabled && setTerm(labelKey)}
      >
        <CardActionArea disabled={disabled} sx={{ height: '100%' }}>
          <Typography variant="body2" sx={{ fontSize: '0.75rem', textAlign: 'center', px: 1 }}>
            {LABELS[labelKey] || labelKey}
          </Typography>
        </CardActionArea>
      </Card>
    );
  };

  // 13) Gerilim Seviyesi kart render
  const renderVoltageCard = labelKey => {
    const isSel = voltage === labelKey;
    return (
      <Card
        key={labelKey}
        variant="outlined"
        sx={{
          height: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderColor: isSel ? 'primary.main' : 'grey.300',
          backgroundColor: isSel ? 'primary.50' : 'background.paper',
          boxShadow: isSel ? 3 : 0,
          cursor: disabled ? 'default' : 'pointer'
        }}
        onClick={() => !disabled && setVoltage(labelKey)}
      >
        <CardActionArea disabled={disabled} sx={{ height: '100%' }}>
          <Typography variant="body2" sx={{ fontSize: '0.75rem', textAlign: 'center', px: 1 }}>
            {LABELS[labelKey] || labelKey}
          </Typography>
        </CardActionArea>
      </Card>
    );
  };

  return (
    <Paper sx={{ p: 1 }}>
      <Typography variant="h6">Tarife Seçimi</Typography>

      {/* ----------- 1. TARİFE GRUPLARI ----------- */}
      <Grid container spacing={1} sx={{ mt: 0, mb: 0 }}>
        {/* Birinci öncelikli gruplar */}
        {primaryGroups.map(pg => (
          <Grid item xs={6} sm={6} md={4} lg={3} key={pg}>
            {renderPrimaryCard(pg)}
          </Grid>
        ))}

        {/* İkinci öncelikli: tek container, alt alta sub‐kartlar */}
        <Grid item xs={6} sm={6} md={4} lg={3}>
          {renderSecondaryContainer()}
        </Grid>
      </Grid>

      {/* ----------- 2. ZAMAN TİPİ ----------- */}
      <Grid container spacing={1} sx={{ mt: 1, mb: 2 }}>
        {allTimes.map(t => (
          <Grid item xs={6} sm={6} md={4} lg={3} key={t}>
            {renderTimeCard(t)}
          </Grid>
        ))}
      </Grid>



      {/* 3+4. Terim ve Gerilim */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <ToggleButtonGroup
            value={term}
            exclusive
            onChange={handleTermChange}
            aria-label="Terim Tipi"
            fullWidth={!isMobile}
            color="primary"
            size="small"
          >
            {allTerms.map(tm => (
              <ToggleButton key={tm} value={tm} aria-label={LABELS[tm]} sx={{ textTransform: 'none' }}>
                {LABELS[tm]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Grid>

        <Grid item xs={12} sm={6}>
          <ToggleButtonGroup
            value={voltage}
            exclusive
            onChange={handleVoltageChange}
            aria-label="Gerilim Seviyesi"
            fullWidth={!isMobile}
            color="primary"
            size="small"
          >
            {allVoltages.map(vl => (
              <ToggleButton key={vl} value={vl} aria-label={LABELS[vl]} sx={{ textTransform: 'none' }}>
                {LABELS[vl]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Grid>
      </Grid>


      {/* ----------- 5. Seçilen Tarife veya Uyarı Mesajı ----------- */}
      {matchedTariff ? (
        <Box sx={{ mt: 3 }}>
          <Typography color="primary">
            Seçilen Tarife: {matchedTariff.name}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mt: 3 }}>
          <Typography color="error">Uygun tarife bulunamadı.</Typography>
        </Box>
      )}

      {/* ----------- 6. Yardımcı Metin (HelperText) ----------- */}
      {helperText && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color={error ? 'error' : 'textSecondary'}>
            {helperText}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default TariffSelector;
